import {
  t,
  ResourceTier,
  RESOURCE_INFO,
  ALL_RESOURCE_TIERS,
  lookupWeapon,
  lookupShield,
  lookupEngine,
  lookupConsumable,
  parseConsumableItemId,
  itemLabel,
  itemDescription,
  resourceNameKey,
} from '@viral-space/shared';
import type { EquipmentState } from '../game/state';
import { attachMobileCloseButton, isMobileLayout } from './ui-shared';
import {
  renderResourceThumbnail,
  renderWeaponThumbnail,
  renderShieldThumbnail,
  renderEngineThumbnail,
  renderConsumableThumbnail,
} from './item-thumbnail';

const COLORS = {
  primary: '#00ff41',
  secondary: '#ffb000',
  bg: 'rgba(0,8,0,0.94)',
  dim: 'rgba(0,255,65,0.3)',
  dimText: 'rgba(0,255,65,0.55)',
  panelBg: 'rgba(0,12,0,0.8)',
  rowHover: 'rgba(0,255,65,0.08)',
} as const;

const FONT = "'Share Tech Mono', monospace";
const GLOW = `0 0 6px ${COLORS.primary}, 0 0 12px rgba(0,255,65,0.3)`;
const BOX_GLOW = `0 0 8px rgba(0,255,65,0.4), inset 0 0 8px rgba(0,255,65,0.05)`;

const TIERS: ResourceTier[] = ALL_RESOURCE_TIERS;

type TabId = 'resources' | 'weapons' | 'shields' | 'engines' | 'consumables';

interface ResourceRowEls {
  qty: HTMLElement;
  value: HTMLElement;
}

interface TabDescriptor {
  id: TabId;
  label: string;
}

export class InventoryPanel {
  private container: HTMLElement;
  private root!: HTMLElement;
  private tabBar!: HTMLElement;
  private tabContent!: HTMLElement;
  private tabButtons = new Map<TabId, HTMLElement>();
  private creditsEl!: HTMLElement;
  private totalValueEl!: HTMLElement;
  private footerEl!: HTMLElement;
  private activeTab: TabId = 'resources';
  private _visible = false;

  private resourceRows = new Map<ResourceTier, ResourceRowEls>();
  private resourceBody!: HTMLElement;
  private resourceEmptyEl!: HTMLElement;

  private currentInventory: { itemId: string; quantity: number }[] = [];
  private currentCredits = 0;

  onEquip: ((slot: 'weapon' | 'shield' | 'engine', itemId: string) => void) | null = null;
  onUse: ((consumableId: string) => void) | null = null;
  // Fired when the panel closes itself (e.g. the mobile ✕ button). The
  // game uses this hook to re-enable input, since the panel was opened
  // with `input.disable()` and the ✕ button doesn't go through the
  // keyboard / touch callbacks that would normally pair the enable.
  onClose: (() => void) | null = null;
  getEquipment: (() => EquipmentState) | null = null;
  /**
   * Live credits accessor. The credits displayed in the inventory header
   * should match the credit counter on the main HUD at all times — even
   * when credits change via paths that don't emit an `InventoryUpdate`
   * (warp payments, mission rewards, sales). Wiring a getter from the
   * game state lets us read the authoritative value at render time so
   * the panel never falls out of sync with the HUD.
   */
  getCredits: (() => number) | null = null;

  constructor(container: HTMLElement) {
    this.container = container;
    this.build();
  }

  get visible(): boolean {
    return this._visible;
  }

  toggle(): void {
    this._visible ? this.hide() : this.show();
  }

  show(): void {
    this._visible = true;
    this.refresh();
    this.root.style.display = 'flex';
  }

  hide(): void {
    this._visible = false;
    this.root.style.display = 'none';
  }

  updateData(
    inventory: { itemId: string; quantity: number }[],
    credits: number,
  ): void {
    this.currentInventory = inventory;
    this.currentCredits = credits;
    if (this._visible) this.refresh();
  }

  /**
   * Cheap updates safe to call every frame: header credits + resource-tier
   * quantities. Does **not** rebuild the active tab — a full
   * `renderActiveTab()` clears `innerHTML` and would destroy Equip / Use
   * buttons between pointerdown and click, making them feel dead.
   */
  refreshNow(): void {
    if (this._visible) this.refreshHeaderAndResources();
  }

  /**
   * Rebuild the visible tab (weapons / shields / engines / consumables /
   * resources shell). Call when equipment or inventory rows change, not
   * on every animation frame.
   */
  redrawActiveTab(): void {
    if (this._visible) this.renderActiveTab();
  }

  private refreshHeaderAndResources(): void {
    const credits = this.getCredits?.() ?? this.currentCredits;
    this.creditsEl.textContent = credits.toLocaleString();
    this.renderResourceRows();
  }

  private refresh(): void {
    this.refreshHeaderAndResources();
    this.renderActiveTab();
  }

  private renderResourceRows(): void {
    const counts = new Map<ResourceTier, number>();
    for (const item of this.currentInventory) {
      const match = item.itemId.match(/^resource_(\d+)$/);
      if (match) {
        const tier = parseInt(match[1], 10) as ResourceTier;
        counts.set(tier, (counts.get(tier) ?? 0) + item.quantity);
      }
    }

    let totalValue = 0;
    let hasAny = false;

    for (const tier of TIERS) {
      const qty = counts.get(tier) ?? 0;
      const info = RESOURCE_INFO[tier];
      const rowEls = this.resourceRows.get(tier);
      if (!rowEls) continue;

      rowEls.qty.textContent = String(qty);
      const value = qty * info.baseValue;
      rowEls.value.textContent = value.toLocaleString();
      totalValue += value;
      if (qty > 0) hasAny = true;
    }

    this.totalValueEl.textContent = totalValue.toLocaleString();
    this.resourceEmptyEl.style.display = hasAny ? 'none' : 'block';
  }

  private renderActiveTab(): void {
    for (const [id, btn] of this.tabButtons) {
      const active = id === this.activeTab;
      btn.style.color = active ? COLORS.primary : COLORS.dimText;
      btn.style.background = active ? COLORS.panelBg : 'transparent';
      btn.style.textShadow = active ? GLOW : 'none';
      btn.style.borderBottomColor = active ? 'transparent' : COLORS.dim;
    }

    this.tabContent.innerHTML = '';
    switch (this.activeTab) {
      case 'resources':
        this.tabContent.appendChild(this.resourceBody);
        this.footerEl.style.display = 'flex';
        break;
      case 'weapons':
        this.renderEquipmentList('weapon');
        this.footerEl.style.display = 'none';
        break;
      case 'shields':
        this.renderEquipmentList('shield');
        this.footerEl.style.display = 'none';
        break;
      case 'engines':
        this.renderEquipmentList('engine');
        this.footerEl.style.display = 'none';
        break;
      case 'consumables':
        this.renderConsumables();
        this.footerEl.style.display = 'none';
        break;
    }
  }

  private switchTab(id: TabId): void {
    this.activeTab = id;
    this.renderActiveTab();
  }

  private renderConsumables(): void {
    type Entry = {
      id: string;
      qty: number;
      def: ReturnType<typeof lookupConsumable>;
      tier: number;
      requiredLevel: number;
      creditCost: number;
      name: string;
    };
    const entries: Entry[] = [];
    for (const item of this.currentInventory) {
      const defId = parseConsumableItemId(item.itemId);
      if (!defId) continue;
      if (item.quantity <= 0) continue;
      const def = lookupConsumable(defId);
      entries.push({
        id: defId,
        qty: item.quantity,
        def,
        tier: def?.tier ?? 0,
        requiredLevel: def?.requiredLevel ?? 0,
        creditCost: def?.creditCost ?? 0,
        name: def ? itemLabel('consumable', defId, def.name) : defId,
      });
    }

    if (entries.length === 0) {
      this.tabContent.appendChild(this.buildEmpty(t('consumable.empty')));
      return;
    }

    // Same ascending order as the equipment tabs: cheap utility kits at
    // the top, end-game expensive insurance items at the bottom.
    entries.sort((a, b) =>
      a.tier - b.tier
      || a.requiredLevel - b.requiredLevel
      || a.creditCost - b.creditCost
      || a.name.localeCompare(b.name),
    );

    for (const entry of entries) {
      const def = entry.def;
      const row = this.buildListRow();
      row.appendChild(this.buildItemThumbnail(() => {
        const cv = this.makeThumbCanvas();
        requestAnimationFrame(() => renderConsumableThumbnail(cv, entry.id));
        return cv;
      }));
      const info = this.buildRowInfo();
      info.appendChild(
        this.el('div', {
          fontSize: 'clamp(11px, 1.5vw, 13px)',
          textShadow: GLOW,
          color: COLORS.primary,
          letterSpacing: '1px',
        }, `x${entry.qty}  ${def ? itemLabel('consumable', entry.id, def.name) : entry.id}`),
      );
      if (def) {
        info.appendChild(
          this.el('div', {
            fontSize: 'clamp(9px, 1.2vw, 11px)',
            color: COLORS.dimText,
            textTransform: 'none',
            letterSpacing: '0.5px',
          }, itemDescription('consumable', entry.id, def.description)),
        );
      }
      row.appendChild(info);

      const btn = this.buildActionButton(t('consumable.use'), COLORS.secondary, () => {
        this.onUse?.(entry.id);
      });
      row.appendChild(btn);

      this.tabContent.appendChild(row);
    }
  }

  private renderEquipmentList(slot: 'weapon' | 'shield' | 'engine'): void {
    const eq = this.getEquipment?.();
    if (!eq) {
      this.tabContent.appendChild(this.buildEmpty(t('inventory.empty')));
      return;
    }

    // Key ratio shown under each item so the pilot can tell a `50 DMG /
    // 4 RATE / 200 RNG` blaster from a `12 DMG / 7 RATE / 240 RNG`
    // laser at a glance. A raw description alone was not enough to
    // compare equipment.
    type StatEntry = { label: string; value: string; delta?: number };
    type Item = {
      id: string;
      remaining: number;
      max: number;
      equipped: boolean;
      name: string;
      desc: string;
      stats: StatEntry[];
      // Sort key fields. We weight by `(tier, requiredLevel, creditCost)`
      // ascending so the list always reads "starter gear at the top,
      // end-game gear at the bottom" — with the equipped item left in
      // its natural sorted slot (the ▶ marker + amber colour are enough
      // to make it stand out without pinning it to the top).
      tier: number;
      requiredLevel: number;
      creditCost: number;
    };

    let items: Item[] = [];
    if (slot === 'weapon') {
      const equippedDef = eq.equippedWeaponId ? lookupWeapon(eq.equippedWeaponId) : null;
      items = eq.ownedWeapons.map((w) => {
        const def = lookupWeapon(w.id);
        const defMax = def?.maxAmmo ?? -1;
        const displayMax = defMax < 0 ? -1 : Math.max(defMax, w.ammo);
        return {
          id: w.id,
          remaining: w.ammo,
          max: displayMax,
          equipped: w.id === eq.equippedWeaponId,
          name: def ? itemLabel('weapon', w.id, def.name) : w.id,
          desc: def ? itemDescription('weapon', w.id, def.description ?? '') : '',
          stats: def ? this.buildWeaponStats(def, equippedDef) : [],
          tier: def?.tier ?? 0,
          requiredLevel: def?.requiredLevel ?? 0,
          creditCost: def?.creditCost ?? 0,
        };
      });
    } else if (slot === 'shield') {
      const equippedDef = eq.equippedShieldId ? lookupShield(eq.equippedShieldId) : null;
      items = eq.ownedShields.map((s) => {
        const def = lookupShield(s.id);
        const defMax = def?.maxCharges ?? -1;
        const displayMax = defMax < 0 ? -1 : Math.max(defMax, s.charges);
        return {
          id: s.id,
          remaining: s.charges,
          max: displayMax,
          equipped: s.id === eq.equippedShieldId,
          name: def ? itemLabel('shield', s.id, def.name) : s.id,
          desc: def ? itemDescription('shield', s.id, def.description ?? '') : '',
          stats: def ? this.buildShieldStats(def, equippedDef) : [],
          tier: def?.tier ?? 0,
          requiredLevel: def?.requiredLevel ?? 0,
          creditCost: def?.creditCost ?? 0,
        };
      });
    } else {
      const equippedDef = eq.equippedEngineId ? lookupEngine(eq.equippedEngineId) : null;
      items = (eq.ownedEngines ?? []).map((e) => {
        const def = lookupEngine(e.id);
        return {
          id: e.id,
          remaining: -1,
          max: -1,
          equipped: e.id === eq.equippedEngineId,
          name: def ? itemLabel('engine', e.id, def.name) : e.id,
          desc: def ? itemDescription('engine', e.id, def.description ?? '') : '',
          stats: def ? this.buildEngineStats(def, equippedDef) : [],
          tier: def?.tier ?? 0,
          requiredLevel: def?.requiredLevel ?? 0,
          creditCost: def?.creditCost ?? 0,
        };
      });
    }

    if (items.length === 0) {
      this.tabContent.appendChild(this.buildEmpty(t('inventory.empty')));
      return;
    }

    items.sort((a, b) =>
      a.tier - b.tier
      || a.requiredLevel - b.requiredLevel
      || a.creditCost - b.creditCost
      || a.name.localeCompare(b.name),
    );

    for (const it of items) {
      const row = this.buildListRow();
      row.appendChild(this.buildItemThumbnail(() => {
        const cv = this.makeThumbCanvas();
        const id = it.id;
        requestAnimationFrame(() => {
          if (slot === 'weapon') renderWeaponThumbnail(cv, id);
          else if (slot === 'shield') renderShieldThumbnail(cv, id);
          else renderEngineThumbnail(cv, id);
        });
        return cv;
      }));
      const info = this.buildRowInfo();
      info.appendChild(
        this.el('div', {
          fontSize: 'clamp(11px, 1.5vw, 13px)',
          textShadow: GLOW,
          color: it.equipped ? COLORS.secondary : COLORS.primary,
          letterSpacing: '1px',
        }, (it.equipped ? '\u25B6 ' : '') + it.name),
      );
      info.appendChild(
        this.el('div', {
          fontSize: 'clamp(9px, 1.2vw, 11px)',
          color: COLORS.dimText,
          textTransform: 'none',
          letterSpacing: '0.5px',
        }, `${it.remaining < 0 ? '\u221E' : `${it.remaining}/${it.max}`}  -  ${it.desc}`),
      );
      if (it.stats.length > 0) {
        info.appendChild(this.buildStatsRow(it.stats, it.equipped));
      }
      row.appendChild(info);

      if (!it.equipped) {
        row.appendChild(
          this.buildActionButton(t('inventory.equip'), COLORS.primary, () => {
            this.onEquip?.(slot, it.id);
          }),
        );
      } else {
        row.appendChild(
          this.el('span', {
            fontSize: 'clamp(9px, 1.2vw, 11px)',
            color: COLORS.secondary,
            letterSpacing: '2px',
            whiteSpace: 'nowrap',
          }, t('inventory.equipped')),
        );
      }

      this.tabContent.appendChild(row);
    }
  }

  /**
   * Format a number for inline display: integers stay as-is, fractional
   * values get a single decimal. Avoids `.toFixed(1)` noise on round
   * stats like DMG:5 becoming "5.0".
   */
  private fmtNum(n: number): string {
    if (!Number.isFinite(n)) return '—';
    return Math.abs(n % 1) < 0.05 ? String(Math.round(n)) : n.toFixed(1);
  }

  /**
   * Compute the delta (as a ±number or ±% when appropriate) between a
   * candidate stat and the equipped item's equivalent. Higher-is-better
   * stats pass `higherBetter: true`; cost-style stats (energy drain,
   * regen delay) pass `higherBetter: false`.
   */
  private buildDelta(candidate: number, equipped: number | null): number | undefined {
    if (equipped === null || equipped === undefined) return undefined;
    if (!Number.isFinite(candidate) || !Number.isFinite(equipped)) return undefined;
    const d = candidate - equipped;
    if (Math.abs(d) < 0.05) return undefined;
    return d;
  }

  private buildWeaponStats(
    def: NonNullable<ReturnType<typeof lookupWeapon>>,
    equipped: ReturnType<typeof lookupWeapon> | null,
  ): { label: string; value: string; delta?: number }[] {
    return [
      {
        label: t('inventory.statDamage'),
        value: this.fmtNum(def.damage),
        delta: this.buildDelta(def.damage, equipped?.damage ?? null),
      },
      {
        label: t('inventory.statFireRate'),
        value: this.fmtNum(def.fireRate),
        delta: this.buildDelta(def.fireRate, equipped?.fireRate ?? null),
      },
      {
        label: t('inventory.statRange'),
        value: this.fmtNum(def.range),
        delta: this.buildDelta(def.range, equipped?.range ?? null),
      },
      {
        label: t('inventory.statEnergy'),
        value: this.fmtNum(def.energyCost),
        // Energy cost is a *cost* — negative delta is the improvement.
        delta: this.buildDelta(def.energyCost, equipped?.energyCost ?? null),
      },
    ];
  }

  private buildShieldStats(
    def: NonNullable<ReturnType<typeof lookupShield>>,
    equipped: ReturnType<typeof lookupShield> | null,
  ): { label: string; value: string; delta?: number }[] {
    const r = def.resistances;
    const eqr = equipped?.resistances;
    // Resistances are 0-1 fractions in the data; show as percentages for
    // human legibility. The three-letter K/E/X column is packed into a
    // single entry so it doesn't blow up the row on small screens.
    const resistValue =
      `${t('inventory.statResistKinetic')}${Math.round((r.kinetic ?? 0) * 100)} ` +
      `${t('inventory.statResistEnergy')}${Math.round((r.energy ?? 0) * 100)} ` +
      `${t('inventory.statResistExplosive')}${Math.round((r.explosive ?? 0) * 100)}`;
    return [
      {
        label: t('inventory.statShieldHp'),
        value: this.fmtNum(def.maxShield),
        delta: this.buildDelta(def.maxShield, equipped?.maxShield ?? null),
      },
      {
        label: t('inventory.statRegen'),
        value: this.fmtNum(def.regenRate),
        delta: this.buildDelta(def.regenRate, equipped?.regenRate ?? null),
      },
      {
        label: t('inventory.statRegenDelay'),
        value: this.fmtNum(def.regenDelay),
        // Regen *delay* is a cost — but since negative deltas are red and
        // positive are green by default, we leave the plain diff here
        // and let the pilot read it. (Omitting the delta avoids
        // confusion; most shields don't differ enough on this stat to
        // matter in ranking decisions.)
        delta: undefined,
      },
      {
        label: t('inventory.statResist'),
        value: resistValue,
        delta: eqr
          ? this.buildDelta(
              (r.kinetic + r.energy + r.explosive) / 3,
              (eqr.kinetic + eqr.energy + eqr.explosive) / 3,
            )
          : undefined,
      },
    ];
  }

  private buildEngineStats(
    def: NonNullable<ReturnType<typeof lookupEngine>>,
    equipped: ReturnType<typeof lookupEngine> | null,
  ): { label: string; value: string; delta?: number }[] {
    return [
      {
        label: t('inventory.statSpeed'),
        value: this.fmtNum(def.maxSpeed),
        delta: this.buildDelta(def.maxSpeed, equipped?.maxSpeed ?? null),
      },
      {
        label: t('inventory.statAccel'),
        value: this.fmtNum(def.acceleration),
        delta: this.buildDelta(def.acceleration, equipped?.acceleration ?? null),
      },
      {
        label: t('inventory.statBoost'),
        value: `x${this.fmtNum(def.boostMultiplier)}`,
        delta: this.buildDelta(def.boostMultiplier, equipped?.boostMultiplier ?? null),
      },
      {
        label: t('inventory.statEnergy'),
        value: this.fmtNum(def.boostEnergyCost),
        delta: this.buildDelta(def.boostEnergyCost, equipped?.boostEnergyCost ?? null),
      },
    ];
  }

  /**
   * Compact "LABEL value (±delta)" grid shown under each equipment
   * entry. The grid is responsive: chips wrap naturally when the row
   * is narrow. The equipped item shows amber values (matching the ▶
   * title row); unequipped items show green values with a coloured
   * ±delta vs. the equipped baseline so the pilot can tell which side
   * is an upgrade without opening both items in a modal.
   */
  private buildStatsRow(
    stats: { label: string; value: string; delta?: number }[],
    isEquipped: boolean,
  ): HTMLElement {
    const row = this.el('div', {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '10px',
      marginTop: '4px',
      fontSize: 'clamp(9px, 1.1vw, 11px)',
      textTransform: 'none',
      letterSpacing: '0.5px',
    });
    for (const s of stats) {
      const chip = this.el('span', {
        display: 'inline-flex',
        gap: '4px',
        alignItems: 'baseline',
      });
      chip.appendChild(
        this.el('span', { color: COLORS.dimText, letterSpacing: '1px' }, s.label),
      );
      chip.appendChild(
        this.el(
          'span',
          {
            color: isEquipped ? COLORS.secondary : COLORS.primary,
            fontWeight: '600',
          },
          s.value,
        ),
      );
      if (!isEquipped && s.delta !== undefined) {
        const positive = s.delta > 0;
        chip.appendChild(
          this.el(
            'span',
            {
              color: positive ? '#5ff066' : '#ff6666',
              fontSize: 'clamp(8px, 1vw, 10px)',
              letterSpacing: '0.5px',
            },
            `${positive ? '+' : ''}${this.fmtNum(s.delta)}`,
          ),
        );
      }
      row.appendChild(chip);
    }
    return row;
  }

  private buildListRow(): HTMLElement {
    return this.el('div', {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '12px',
      padding: 'clamp(8px, 1.2vw, 12px) clamp(12px, 2vw, 20px)',
      borderBottom: `1px solid rgba(0,255,65,0.08)`,
      transition: 'background 0.15s',
    });
  }

  /**
   * Render a 40x40 thumbnail canvas inside a wrapper that keeps it from
   * being squished by the list row's flex layout. Defers the actual
   * 3D draw with `requestAnimationFrame` (via the caller) so the 21+
   * shop / inventory rows don't all hammer the shared offscreen
   * renderer in the same frame.
   */
  private buildItemThumbnail(makeCanvas: () => HTMLCanvasElement): HTMLElement {
    const wrap = this.el('div', {
      width: '40px',
      height: '40px',
      flexShrink: '0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    });
    wrap.appendChild(makeCanvas());
    return wrap;
  }

  private makeThumbCanvas(): HTMLCanvasElement {
    const cv = document.createElement('canvas');
    cv.width = 40;
    cv.height = 40;
    cv.style.width = '40px';
    cv.style.height = '40px';
    cv.style.display = 'block';
    return cv;
  }

  private buildRowInfo(): HTMLElement {
    return this.el('div', {
      display: 'flex',
      flexDirection: 'column',
      gap: '3px',
      flex: '1',
      minWidth: '0',
    });
  }

  private buildActionButton(
    label: string,
    color: string,
    onClick: () => void,
  ): HTMLButtonElement {
    const btn = this.el('button', {
      fontFamily: FONT,
      fontSize: 'clamp(10px, 1.3vw, 12px)',
      padding: '8px 16px',
      background: 'transparent',
      border: `1px solid ${color}`,
      color,
      cursor: 'pointer',
      textTransform: 'uppercase',
      letterSpacing: '2px',
      pointerEvents: 'auto',
      flexShrink: '0',
      transition: 'all 0.15s',
    }, label) as HTMLButtonElement;
    btn.addEventListener('mouseenter', () => {
      btn.style.background = color;
      btn.style.color = '#000';
    });
    btn.addEventListener('mouseleave', () => {
      btn.style.background = 'transparent';
      btn.style.color = color;
    });
    btn.addEventListener('click', onClick);
    return btn;
  }

  private buildEmpty(text: string): HTMLElement {
    return this.el('div', {
      padding: '32px 20px',
      textAlign: 'center',
      fontSize: 'clamp(11px, 1.5vw, 13px)',
      color: COLORS.dim,
      letterSpacing: '2px',
    }, text);
  }

  private el(
    tag: string,
    styles: Partial<CSSStyleDeclaration>,
    text?: string,
  ): HTMLElement {
    const e = document.createElement(tag);
    Object.assign(e.style, styles);
    if (text !== undefined) e.textContent = text;
    return e;
  }

  private build(): void {
    // Full-screen overlay on every viewport. On mobile this fills the
    // entire landscape viewport so small screens aren't forced to pan;
    // on desktop it replaces the previous centered dialog with a
    // tabbed workspace that has enough room for the equipment lists
    // without the old vertical scroll stack.
    this.root = this.el('div', {
      position: 'absolute',
      inset: '0',
      display: 'none',
      flexDirection: 'column',
      fontFamily: FONT,
      color: COLORS.primary,
      textTransform: 'uppercase',
      letterSpacing: '1px',
      zIndex: '85',
      pointerEvents: 'auto',
      background: COLORS.bg,
      overflow: 'hidden',
      paddingTop: 'env(safe-area-inset-top, 0px)',
      paddingRight: 'env(safe-area-inset-right, 0px)',
      paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      paddingLeft: 'env(safe-area-inset-left, 0px)',
    });
    this.container.appendChild(this.root);

    attachMobileCloseButton(this.root, () => {
      this.hide();
      this.onClose?.();
    });

    this.buildHeader();
    this.buildTabBar();
    this.buildContentArea();
    this.buildResourcesPanel();
    this.buildFooter();

    this.switchTab('resources');
  }

  private buildHeader(): void {
    const mobile = isMobileLayout();
    const header = this.el('div', {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '16px',
      // On mobile the corner ✕ (attached by attachMobileCloseButton)
      // sits at top/right 6px with a 36px hit area, so we reserve ~52px
      // of right-padding in the header so the credits value doesn't
      // slide underneath it. On the "[I / ESC]" hint block we also
      // hide the desktop keybind on mobile where it means nothing.
      padding: mobile
        ? '10px 56px 10px 14px'
        : 'clamp(10px, 1.8vw, 18px) clamp(16px, 3vw, 32px)',
      borderBottom: `1px solid ${COLORS.dim}`,
      background: 'rgba(0,255,65,0.04)',
      flexShrink: '0',
    });

    const titleBlock = this.el('div', {
      display: 'flex',
      flexDirection: 'column',
      gap: '2px',
      minWidth: '0',
    });
    titleBlock.appendChild(
      this.el('div', {
        fontSize: mobile ? '14px' : 'clamp(14px, 2vw, 22px)',
        letterSpacing: mobile ? '2px' : 'clamp(2px, 0.5vw, 5px)',
        textShadow: GLOW,
        whiteSpace: 'nowrap',
      }, `// ${t('inventory.title')} //`),
    );
    titleBlock.appendChild(
      this.el('div', {
        fontSize: '10px',
        color: COLORS.dimText,
        letterSpacing: '2px',
        display: mobile ? 'none' : '',
      }, '[I / ESC]'),
    );
    header.appendChild(titleBlock);

    const creditBlock = this.el('div', {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      gap: '2px',
      flexShrink: '0',
    });
    creditBlock.appendChild(
      this.el('span', {
        fontSize: mobile ? '8px' : '10px',
        color: COLORS.dimText,
        letterSpacing: mobile ? '2px' : '3px',
      }, t('hud.credits')),
    );
    this.creditsEl = this.el('span', {
      fontSize: mobile ? '14px' : 'clamp(16px, 2.2vw, 24px)',
      color: COLORS.secondary,
      textShadow: `0 0 6px ${COLORS.secondary}`,
      letterSpacing: mobile ? '1px' : '2px',
    }, '0');
    creditBlock.appendChild(this.creditsEl);
    header.appendChild(creditBlock);

    this.root.appendChild(header);
  }

  private buildTabBar(): void {
    this.tabBar = this.el('div', {
      display: 'flex',
      gap: '2px',
      padding: '8px clamp(12px, 2vw, 24px) 0',
      flexShrink: '0',
      overflowX: 'auto',
      borderBottom: `1px solid ${COLORS.dim}`,
    });
    this.root.appendChild(this.tabBar);

    const tabs: TabDescriptor[] = [
      { id: 'resources', label: t('inventory.resources') },
      { id: 'weapons', label: t('inventory.sectionWeapons') },
      { id: 'shields', label: t('inventory.sectionShields') },
      { id: 'engines', label: t('inventory.sectionEngines') },
      { id: 'consumables', label: t('consumable.inventoryHeading') },
    ];

    for (const tab of tabs) {
      const btn = this.el('button', {
        fontFamily: FONT,
        fontSize: 'clamp(10px, 1.3vw, 13px)',
        padding: 'clamp(8px, 1.2vw, 12px) clamp(12px, 2vw, 22px)',
        background: 'transparent',
        border: `1px solid ${COLORS.dim}`,
        borderBottom: 'none',
        color: COLORS.dimText,
        cursor: 'pointer',
        textTransform: 'uppercase',
        letterSpacing: '1.5px',
        transition: 'color 0.15s, background 0.15s',
        pointerEvents: 'auto',
        whiteSpace: 'nowrap',
        flexShrink: '0',
        // `manipulation` disables iOS Safari's 300ms double-tap-to-zoom
        // delay. Without it, rapid successive taps on tab buttons after
        // the tab content is re-rendered can get dropped by WebKit's
        // click-synthesis heuristic. `userSelect: 'none'` stops a long
        // tap from starting a selection on the label.
        touchAction: 'manipulation',
        userSelect: 'none',
        webkitUserSelect: 'none',
        webkitTapHighlightColor: 'transparent',
      } as Partial<CSSStyleDeclaration>, tab.label);
      // Fire on `pointerdown` rather than `click` so the tab responds
      // on the initial finger contact — this avoids a WebKit edge case
      // where, after the tab content is rebuilt (innerHTML = ''), the
      // synthetic click for the next tap can get swallowed. Using
      // pointerdown also feels snappier. We stopPropagation so the
      // event doesn't reach parent handlers (there are none today, but
      // future HUD code adding document-level pointerdown listeners
      // won't accidentally eat a tab switch).
      const activate = (e: Event) => {
        e.preventDefault();
        e.stopPropagation();
        this.switchTab(tab.id);
      };
      btn.addEventListener('pointerdown', activate);
      // Keyboard accessibility — preserve the synthesized click path
      // for screen readers and Enter/Space on focused tabs.
      btn.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          activate(e);
        }
      });
      this.tabBar.appendChild(btn);
      this.tabButtons.set(tab.id, btn);
    }
  }

  private buildContentArea(): void {
    this.tabContent = this.el('div', {
      flex: '1',
      // `minHeight: 0` is required inside a flex column — without it the
      // default `minHeight: auto` makes the container grow to the size
      // of its children and the parent loses the "constrained height"
      // that scrolling relies on, so on mobile the last items can be
      // clipped past the bottom edge.
      minHeight: '0',
      display: 'flex',
      flexDirection: 'column',
      background: COLORS.panelBg,
      border: `1px solid ${COLORS.dim}`,
      borderTop: 'none',
      boxShadow: BOX_GLOW,
      margin: '0 clamp(12px, 2vw, 24px) clamp(8px, 1.5vw, 16px)',
      overflowY: 'auto',
      overflowX: 'hidden',
      // Trap scroll momentum inside the panel. Without `contain`, when
      // the pilot reaches the bottom of the list on iOS the overscroll
      // rubber-band leaks to ancestor elements and the inner list
      // snaps back up to the top — hiding the last items exactly when
      // the pilot is trying to read them.
      overscrollBehavior: 'contain',
      // `touchAction: pan-y` tells WebKit this area only scrolls
      // vertically, so it never tries to treat a downward drag at the
      // end of the list as a horizontal swipe or page-level gesture.
      touchAction: 'pan-y',
      pointerEvents: 'auto',
      // Leave a small gutter after the last row so the end-of-list
      // entry sits clear of the container's inner border instead of
      // bumping directly against it (which visually reinforced the
      // "clipped" look while the pilot was overscrolling).
      paddingBottom: '12px',
    } as Partial<CSSStyleDeclaration>);
    this.root.appendChild(this.tabContent);
  }

  private buildResourcesPanel(): void {
    // Built once and reused — we detach/re-attach it whenever the
    // resources tab becomes active so the resource row elements (and
    // their references in `this.resourceRows`) survive tab switching.
    this.resourceBody = this.el('div', {
      display: 'flex',
      flexDirection: 'column',
    });

    // The first column hosts a small 3D thumbnail of the resource so
    // every tier reads visually distinct beyond just colour. The
    // thumbnails share a single offscreen WebGL context (see
    // `ui/item-thumbnail.ts`) so 21 of them on screen does not exhaust
    // the browser's WebGL budget.
    const gridColumns = '40px minmax(0, 1fr) 90px 110px';

    const colHeader = this.el('div', {
      display: 'grid',
      gridTemplateColumns: gridColumns,
      gap: '12px',
      padding: 'clamp(10px, 1.4vw, 14px) clamp(12px, 2vw, 20px)',
      borderBottom: `1px solid ${COLORS.dim}`,
      fontSize: 'clamp(9px, 1.1vw, 11px)',
      color: COLORS.dim,
      letterSpacing: '2px',
      position: 'sticky',
      top: '0',
      background: COLORS.panelBg,
    });
    colHeader.appendChild(this.el('span', {}));
    colHeader.appendChild(this.el('span', {}, t('inventory.resources')));
    colHeader.appendChild(this.el('span', { textAlign: 'right' }, 'QTY'));
    colHeader.appendChild(this.el('span', { textAlign: 'right' }, t('inventory.value')));
    this.resourceBody.appendChild(colHeader);

    for (const tier of TIERS) {
      const info = RESOURCE_INFO[tier];
      const colorHex = `#${info.color.toString(16).padStart(6, '0')}`;
      const name = t(resourceNameKey(tier));

      const row = this.el('div', {
        display: 'grid',
        gridTemplateColumns: gridColumns,
        gap: '12px',
        padding: 'clamp(10px, 1.4vw, 14px) clamp(12px, 2vw, 20px)',
        alignItems: 'center',
        borderBottom: `1px solid rgba(0,255,65,0.08)`,
        transition: 'background 0.15s',
      });
      row.addEventListener('mouseenter', () => {
        row.style.background = COLORS.rowHover;
      });
      row.addEventListener('mouseleave', () => {
        row.style.background = 'transparent';
      });

      const thumb = document.createElement('canvas');
      thumb.width = 32;
      thumb.height = 32;
      thumb.style.width = '32px';
      thumb.style.height = '32px';
      thumb.style.display = 'block';
      thumb.style.flexShrink = '0';
      thumb.title = name;

      const nameEl = this.el('span', {
        fontSize: 'clamp(11px, 1.4vw, 13px)',
        color: colorHex,
        textShadow: `0 0 4px ${colorHex}60`,
        letterSpacing: '1px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }, name);

      const qty = this.el('span', {
        fontSize: 'clamp(12px, 1.4vw, 14px)',
        textAlign: 'right',
        textShadow: GLOW,
      }, '0');

      const value = this.el('span', {
        fontSize: 'clamp(11px, 1.3vw, 13px)',
        textAlign: 'right',
        color: COLORS.secondary,
        letterSpacing: '1px',
      }, '0');

      row.append(thumb, nameEl, qty, value);
      this.resourceBody.appendChild(row);
      this.resourceRows.set(tier, { qty, value });

      // Defer the thumbnail render so we batch the cost across the
      // panel's first paint instead of stalling on a single frame.
      requestAnimationFrame(() => renderResourceThumbnail(thumb, tier));
    }

    this.resourceEmptyEl = this.el('div', {
      padding: '32px 20px',
      textAlign: 'center',
      fontSize: 'clamp(11px, 1.5vw, 13px)',
      color: COLORS.dim,
      display: 'none',
      letterSpacing: '2px',
    }, t('inventory.empty'));
    this.resourceBody.appendChild(this.resourceEmptyEl);
  }

  private buildFooter(): void {
    this.footerEl = this.el('div', {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: 'clamp(10px, 1.5vw, 16px) clamp(16px, 3vw, 32px)',
      borderTop: `1px solid ${COLORS.dim}`,
      background: 'rgba(0,255,65,0.04)',
      flexShrink: '0',
    });
    this.footerEl.appendChild(
      this.el('span', {
        fontSize: 'clamp(10px, 1.2vw, 12px)',
        textShadow: GLOW,
        letterSpacing: '3px',
      }, t('inventory.total')),
    );
    this.totalValueEl = this.el('span', {
      fontSize: 'clamp(13px, 1.7vw, 18px)',
      color: COLORS.secondary,
      textShadow: `0 0 6px ${COLORS.secondary}`,
      letterSpacing: '2px',
    }, '0');
    this.footerEl.appendChild(this.totalValueEl);
    this.root.appendChild(this.footerEl);
  }

  dispose(): void {
    this.root.remove();
  }
}
